import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ExternalLink, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PRESENT = {
  starter: { price_m: "A$29", price_y: "A$232/yr (save 33%)", desc: "Small teams getting started.", features: ["1 workspace","Up to 3 users","50 generations / month","Template uploads","Word export","Document library"] },
  professional: { price_m: "A$99", price_y: "A$792/yr (save 33%)", desc: "Regular business use.", features: ["Up to 15 users","500 generations / month","Multiple templates","Workspace standards","Version history","Priority support"], highlight: true },
  business: { price_m: "A$299", price_y: "A$2,392/yr (save 33%)", desc: "Larger teams and higher volume.", features: ["Up to 50 users","5,000 generations / month","Advanced permissions","Higher limits","Centralised standards","Custom onboarding"] },
};

export default function Billing() {
  const { activeWorkspace } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [cadence, setCadence] = useState("monthly");
  const [busy, setBusy] = useState("");
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    if (!activeWorkspace) return;
    api.get("/payments/plans").then(({data}) => setPlans(data));
    api.get("/payments/status").then(({data}) => setBilling(data)).catch(() => setBilling({ available: false, reason: "unreachable" }));
    api.get(`/workspaces/${activeWorkspace.id}/subscription`).then(({data}) => setSub(data));
  }, [activeWorkspace]);

  const billingAvailable = billing?.available !== false;

  const currentPlan = sub?.plan || "free";

  const checkout = async (planId) => {
    setBusy(planId);
    try {
      const { data } = await api.post("/payments/checkout", {
        plan: planId, cadence, workspace_id: activeWorkspace.id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy("");
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspace.id}/billing-portal`, { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setBusy("");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-8" data-testid="billing-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Billing</h1>
          <p className="text-sm text-slate-400">Manage your Fexidoc subscription.</p>
        </div>
        {sub && sub.status === "active" && (
          <button onClick={openPortal} disabled={busy==="portal"} className="border border-white/10 hover:border-white/20 text-slate-200 px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2" data-testid="manage-billing">
            {busy==="portal" ? <Loader2 size={14} className="animate-spin"/> : <ExternalLink size={14}/>} Manage billing
          </button>
        )}
      </div>

      {/* Current plan */}
      <div className="fx-panel p-5 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">Current plan</div>
            <div className="mt-1 font-display text-xl font-bold text-white capitalize">{sub?.plan || "Free"} {sub?.status === "active" && <span className="text-[11px] mono uppercase tracking-widest text-emerald-300 ml-2">Active</span>}</div>
          </div>
          {sub && sub.limits && (
            <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
              <Limit label="Members" value={sub.limits.max_members}/>
              <Limit label="Generations / mo" value={sub.limits.monthly_generations}/>
              <Limit label="Templates" value={sub.limits.max_templates}/>
            </div>
          )}
        </div>
      </div>

      {!billingAvailable && (
        <div className="fx-panel p-5 mb-4 border-amber-500/40 bg-amber-500/5" data-testid="billing-unavailable">
          <div className="text-slate-100 font-medium">Billing setup is currently unavailable.</div>
          <div className="text-sm text-slate-400 mt-1">
            We can't reach our payment provider right now, so upgrades are paused. Your current plan and limits are unchanged.{" "}
            <Link to="/contact" className="text-cyan-300 hover:text-cyan-200">Contact us</Link> if you need to change plan today.
          </div>
        </div>
      )}

      {/* Cadence toggle */}
      {billingAvailable && (
      <div className="flex items-center gap-2 mb-4">
        <div className="inline-flex bg-[#0D1224] border border-[#1E293B] rounded-lg p-1">
          <button onClick={()=>setCadence("monthly")} className={`px-4 py-1.5 rounded-md text-sm ${cadence==="monthly" ? "bg-white/10 text-white":"text-slate-400 hover:text-white"}`} data-testid="cadence-monthly">Monthly</button>
          <button onClick={()=>setCadence("yearly")} className={`px-4 py-1.5 rounded-md text-sm ${cadence==="yearly" ? "bg-white/10 text-white":"text-slate-400 hover:text-white"}`} data-testid="cadence-yearly">Yearly <span className="text-cyan-300 text-[10px] mono ml-1">-33%</span></button>
        </div>
      </div>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => {
          const pres = PRESENT[p.id] || {};
          const current = currentPlan === p.id;
          return (
            <div key={p.id} className={`fx-panel p-5 flex flex-col ${pres.highlight ? "border-cyan-400/40" : ""} ${current ? "fx-glow-cyan border-cyan-400/60" : ""}`} data-testid={`plan-${p.id}`}>
              {pres.highlight && !current && <div className="mono text-[10px] uppercase tracking-widest text-cyan-300 mb-2">Most popular</div>}
              {current && <div className="mono text-[10px] uppercase tracking-widest text-emerald-300 mb-2">Current plan</div>}
              <div className="font-display font-semibold text-lg text-slate-100">{p.name}</div>
              <div className="mt-2 text-3xl font-display font-extrabold text-white">{cadence==="monthly" ? pres.price_m : pres.price_y}</div>
              <div className="mt-1 text-sm text-slate-400 min-h-[36px]">{pres.desc}</div>
              <ul className="mt-4 space-y-2 flex-1">
                {(pres.features || []).map(f => <li key={f} className="text-sm text-slate-300 flex gap-2"><Check size={14} className="text-cyan-400 mt-0.5"/>{f}</li>)}
              </ul>
              {billingAvailable ? (
                <button
                  disabled={current || busy===p.id}
                  onClick={() => checkout(p.id)}
                  data-testid={`checkout-${p.id}`}
                  className={`mt-6 text-sm px-4 py-2.5 rounded-lg font-medium inline-flex items-center justify-center gap-2 ${current ? "bg-slate-800 text-slate-500 cursor-default" : "fx-btn-primary text-white"}`}
                >
                  {busy===p.id ? <Loader2 size={14} className="animate-spin"/> : null}
                  {current ? "Active" : (busy===p.id ? "Redirecting…" : `Upgrade to ${p.name}`)}
                </button>
              ) : (
                <Link to="/contact" className="mt-6 text-sm px-4 py-2.5 rounded-lg font-medium text-center border border-white/10 hover:border-white/25 text-slate-200" data-testid={`contact-sales-${p.id}`}>Contact sales</Link>
              )}
            </div>
          );
        })}
      </div>

      <div className="fx-panel p-5 mt-6 text-sm text-slate-400">
        All prices are in Australian dollars (AUD) and exclude GST where applicable. Payments are processed securely by Stripe. <Link to="/contact" className="text-cyan-300 hover:text-cyan-200">Contact sales</Link> for enterprise pricing.
      </div>
    </div>
  );
}

function Limit({label, value}) {
  return <div><div className="text-[10px] mono uppercase tracking-widest text-slate-500">{label}</div><div className="text-slate-100 font-semibold">{value}</div></div>;
}
